/**
 * AgenticQuizBlock — inline knowledge-check quiz for Agentic AI Mastery modules.
 *
 * Rendered after the lesson body only when the module is unlocked.
 * Fetches quiz questions on mount; silently renders nothing on 403/404
 * or when count === 0 (no quiz for this module).
 *
 * Flow:
 *   answering → submit (all answered) → results mode → "Try again" resets
 */

import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import QuizIcon from "@mui/icons-material/Quiz";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  getModuleQuiz,
  submitModuleQuiz,
  AgenticQuiz,
  AgenticQuizQuestion,
  AgenticQuizResult,
  AgenticQuizResultItem,
} from "../api/client";

// coral via theme.palette.primary — per-product accent deferred (spec D2)

interface AgenticQuizBlockProps {
  slug: string;
  /** API product namespace (default agentic-mastery); pass "agentic-ops" to reuse for Ops. */
  product?: string;
}

// ---------------------------------------------------------------------------
// Question card — answering mode
// ---------------------------------------------------------------------------

interface QuestionCardProps {
  index: number;
  stem: string;
  options: string[];
  selectedIndex: number | null;
  onChange: (index: number) => void;
}

function QuestionCard({
  index,
  stem,
  options,
  selectedIndex,
  onChange,
}: QuestionCardProps) {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        border: `1px solid`,
        borderColor: selectedIndex !== null
          ? alpha(theme.palette.primary.main, 0.3)
          : "divider",
        borderRadius: 2,
        transition: "border-color 0.2s",
      }}
    >
      <Typography variant="body2" fontWeight={500} sx={{ mb: 1.5 }}>
        {index + 1}. {stem}
      </Typography>
      <FormControl component="fieldset" sx={{ width: "100%" }}>
        <RadioGroup
          value={selectedIndex !== null ? String(selectedIndex) : ""}
          onChange={(e) => onChange(Number(e.target.value))}
        >
          {options.map((opt, i) => (
            <FormControlLabel
              key={i}
              value={String(i)}
              control={
                <Radio
                  size="small"
                  sx={{
                    color: alpha(theme.palette.primary.main, 0.5),
                    "&.Mui-checked": { color: theme.palette.primary.main },
                  }}
                />
              }
              label={
                <Typography variant="body2" sx={{ lineHeight: 1.65 }}>
                  {opt}
                </Typography>
              }
              sx={{
                ml: 0,
                mr: 0,
                py: 0.5,
                px: 1,
                borderRadius: 1,
                transition: "background 0.15s",
                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.04) },
              }}
            />
          ))}
        </RadioGroup>
      </FormControl>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Question card — results mode
// ---------------------------------------------------------------------------

interface ResultCardProps {
  index: number;
  stem: string;
  options: string[];
  result: AgenticQuizResultItem;
}

function ResultCard({ index, stem, options, result }: ResultCardProps) {
  const theme = useTheme();
  const { correct, correctIndex, selectedIndex, rationale } = result;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        border: `1px solid`,
        borderColor: correct ? alpha(theme.palette.success.main, 0.4) : alpha(theme.palette.error.main, 0.35),
        borderRadius: 2,
        bgcolor: correct ? alpha(theme.palette.success.main, 0.03) : alpha(theme.palette.error.main, 0.03),
      }}
    >
      {/* Question + status icon */}
      <Stack direction="row" spacing={1.5} alignItems="flex-start" mb={1.5}>
        {correct ? (
          <CheckCircleOutlineIcon
            sx={{ color: theme.palette.success.main, fontSize: 20, mt: 0.15, flexShrink: 0 }}
          />
        ) : (
          <HighlightOffIcon
            sx={{ color: theme.palette.error.main, fontSize: 20, mt: 0.15, flexShrink: 0 }}
          />
        )}
        <Typography variant="body2" fontWeight={500}>
          {index + 1}. {stem}
        </Typography>
      </Stack>

      {/* Options — highlight selected + correct */}
      <Stack spacing={0.5} mb={1.5} ml={3.5}>
        {options.map((opt, i) => {
          const isSelected = i === selectedIndex;
          const isCorrect = i === correctIndex;
          let bgcolor = "transparent";
          let color = "text.primary";
          let fontWeight: number | string = 400;

          if (isCorrect) {
            bgcolor = alpha(theme.palette.success.main, 0.12);
            color = theme.palette.success.dark;
            fontWeight = 500;
          } else if (isSelected && !isCorrect) {
            bgcolor = alpha(theme.palette.error.main, 0.1);
            color = theme.palette.error.dark;
          }

          return (
            <Box
              key={i}
              sx={{
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                bgcolor,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              {isCorrect && (
                <CheckCircleOutlineIcon
                  sx={{ fontSize: 15, color: theme.palette.success.main, flexShrink: 0 }}
                />
              )}
              {isSelected && !isCorrect && (
                <HighlightOffIcon
                  sx={{ fontSize: 15, color: theme.palette.error.main, flexShrink: 0 }}
                />
              )}
              <Typography
                variant="body2"
                sx={{ color, fontWeight, lineHeight: 1.65 }}
              >
                {opt}
              </Typography>
            </Box>
          );
        })}
      </Stack>

      {/* Rationale */}
      <Box
        sx={{
          ml: 3.5,
          pl: 1.5,
          borderLeft: `3px solid`,
          borderColor: correct ? alpha(theme.palette.success.main, 0.4) : alpha(theme.palette.error.main, 0.3),
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.65 }}>
          {rationale}
        </Typography>
      </Box>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function AgenticQuizBlock({ slug, product = "agentic-mastery" }: AgenticQuizBlockProps) {
  const theme = useTheme();
  const { t } = useTranslation("common");
  // Selected answers: questionId → option index
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<AgenticQuizResult | null>(null);

  const {
    data: quiz,
    isLoading,
    error,
  } = useQuery<AgenticQuiz>({
    queryKey: ["agentic-quiz", product, slug],
    queryFn: () => getModuleQuiz(slug, product),
    retry: false,
    // Never retry on 403/404 — treat those as "no quiz"
    retryOnMount: false,
  });

  // Silently render nothing when quiz doesn't exist or errored
  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
        <CircularProgress size={24} sx={{ color: theme.palette.primary.main }} />
      </Box>
    );
  }

  if (error || !quiz || quiz.count === 0) {
    return null;
  }

  // Explicit param type, not inference: @tanstack/react-query's generic
  // signature tightened in the 5.x line, which degrades quiz.questions to any
  // and makes this callback's `q` an implicit any (TS7006 under noImplicitAny).
  // Annotating pins the type regardless of what useQuery infers.
  const allAnswered = quiz.questions.every(
    (q: AgenticQuizQuestion) => answers[q.id] !== undefined
  );

  async function handleSubmit() {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await submitModuleQuiz(slug, answers, product);
      setResult(res);
    } catch {
      setSubmitError(t("AgenticQuizBlock.submitFailed"));
    } finally {
      setSubmitting(false);
    }
  }

  function handleReset() {
    setAnswers({});
    setResult(null);
    setSubmitError(null);
  }

  // Shared section wrapper — Divider + heading always appear when quiz has content
  const SectionHeader = (
    <>
      <Divider sx={{ mb: 5 }} />
      <Typography
        variant="subtitle1"
        fontWeight={500}
        sx={{ mb: 3, color: "text.primary" }}
      >
        {t("AgenticQuizBlock.knowledgeCheck")}
      </Typography>
    </>
  );

  // ---- Results mode --------------------------------------------------------
  if (result) {
    const resultMap = Object.fromEntries(result.results.map((r) => [r.id, r]));

    return (
      <Box>
        {SectionHeader}
        {/* Score header */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            border: `1px solid`,
            borderColor: result.passed
              ? alpha(theme.palette.success.main, 0.4)
              : alpha(theme.palette.primary.main, 0.25),
            borderRadius: 3,
            bgcolor: result.passed
              ? alpha(theme.palette.success.main, 0.03)
              : alpha(theme.palette.primary.main, 0.03),
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="h6" fontWeight={500} sx={{ mb: 0.5 }}>
                {t("AgenticQuizBlock.scoreCorrect", {
                  score: result.score,
                  total: result.total,
                })}
              </Typography>
              <Chip
                label={
                  result.passed
                    ? t("AgenticQuizBlock.passed")
                    : t("AgenticQuizBlock.notPassed")
                }
                size="small"
                sx={{
                  fontWeight: 500,
                  fontSize: "0.72rem",
                  bgcolor: result.passed
                    ? alpha(theme.palette.success.main, 0.15)
                    : alpha(theme.palette.error.main, 0.12),
                  color: result.passed ? theme.palette.success.dark : theme.palette.error.dark,
                }}
              />
            </Box>
            <Button
              variant="outlined"
              size="small"
              onClick={handleReset}
              sx={{
                textTransform: "none",
                fontWeight: 500,
                borderColor: alpha(theme.palette.primary.main, 0.4),
                color: theme.palette.primary.main,
                "&:hover": {
                  borderColor: theme.palette.primary.main,
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                },
              }}
            >
              {t("AgenticQuizBlock.tryAgain")}
            </Button>
          </Stack>
        </Paper>

        {/* Per-question feedback */}
        <Stack spacing={2}>
          {quiz.questions.map((q, i) => (
            <ResultCard
              key={q.id}
              index={i}
              stem={q.stem}
              options={q.options}
              result={resultMap[q.id]}
            />
          ))}
        </Stack>
      </Box>
    );
  }

  // ---- Answering mode ------------------------------------------------------
  return (
    <Box>
      {SectionHeader}
      {/* Quiz header */}
      <Stack direction="row" spacing={1.5} alignItems="center" mb={3}>
        <QuizIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
        <Typography
          variant="caption"
          fontWeight={700}
          sx={{
            color: theme.palette.primary.main,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
          }}
        >
          {t("AgenticQuizBlock.knowledgeCheckCount", { count: quiz.count })}
        </Typography>
      </Stack>

      <Stack spacing={2} mb={3}>
        {quiz.questions.map((q, i) => (
          <QuestionCard
            key={q.id}
            index={i}
            stem={q.stem}
            options={q.options}
            selectedIndex={answers[q.id] !== undefined ? answers[q.id] : null}
            onChange={(idx) =>
              setAnswers((prev) => ({ ...prev, [q.id]: idx }))
            }
          />
        ))}
      </Stack>

      {submitError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {submitError}
        </Alert>
      )}

      <Button
        variant="contained"
        disabled={!allAnswered || submitting}
        onClick={handleSubmit}
        startIcon={
          submitting ? (
            <CircularProgress size={14} sx={{ color: "inherit" }} />
          ) : undefined
        }
        sx={{
          bgcolor: theme.palette.primary.main,
          fontWeight: 500,
          textTransform: "none",
          "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) },
          "&.Mui-disabled": { bgcolor: alpha(theme.palette.primary.main, 0.35), color: "white" },
        }}
      >
        {submitting
          ? t("AgenticQuizBlock.submitting")
          : t("AgenticQuizBlock.submitAnswers")}
      </Button>

      {!allAnswered && (
        <Typography
          variant="caption"
          color="text.disabled"
          sx={{ display: "block", mt: 1 }}
        >
          {t("AgenticQuizBlock.answerAllToSubmit")}
        </Typography>
      )}
    </Box>
  );
}
